import type { RasterLayerSource } from '@/hooks/useDatasets';
import { releaseTilesPath } from './releaseTiles';
import type { LayerConfig } from './types';
import { LayerType } from './types';
import { TileLayer } from '@deck.gl/geo-layers';
import { BitmapLayer } from '@deck.gl/layers';
import { GL } from '@luma.gl/constants';
import { getGeoBoundingBox } from '@/views/map/utils';
import { OVERVIEW_ZOOM_THRESHOLD } from '@/app/constants';

// Raw pixels are uint16 — 0 encodes score 0.0 and 65535 encodes 1.0. We feed
// the tiler a rescale range derived from the client's max score so the
// purple→yellow gradient spans the values they actually have.
const UINT16_MAX = 65535;

const toUint16Rescale = (maxScore: number | null | undefined): number => {
  if (maxScore == null || maxScore <= 0) {
    return UINT16_MAX;
  }
  // Clamp to [1, UINT16_MAX]: a maxScore below ~1/65535 would otherwise round
  // to 0 and produce rescale=0,0, which the tiler treats as an invalid range.
  return Math.min(UINT16_MAX, Math.max(1, Math.round(maxScore * UINT16_MAX)));
};

export const getFuelHazardLayer = (
  source: RasterLayerSource,
  config: LayerConfig,
  isOverview: boolean,
  maxScore?: number | null,
  minZoom = OVERVIEW_ZOOM_THRESHOLD,
  maxZoom = 18
) => {
  const rescaleMax = toUint16Rescale(maxScore);

  return new TileLayer({
    id: LayerType[LayerType.fuel_hazard],
    data: `${releaseTilesPath(source, 'webp')}?pixel_selection=first&return_mask=true&bidx=1&resampling=nearest&colormap_name=fuel_hazard_colormap&rescale=0,${rescaleMax}`,
    loadOptions: {
      fetch: {
        credentials: 'include',
      },
    },
    minZoom,
    maxZoom,
    ...config,
    opacity: isOverview ? 1 : 1,
    refinementStrategy: 'no-overlap',
    renderSubLayers: (props) => {
      if (props.data === null) {
        return null;
      }

      const boundingBox = getGeoBoundingBox(props);
      if (!boundingBox) {
        return null;
      }
      const { west, south, east, north } = boundingBox;

      return new BitmapLayer({
        id: props.id,
        image: props.data,
        bounds: [west, south, east, north],
        textureParameters: {
          [GL.TEXTURE_MIN_FILTER]: GL.NEAREST,
          [GL.TEXTURE_MAG_FILTER]: GL.NEAREST,
        },
        transparentColor: [255, 255, 255, 0],
        pickable: props.pickable,
        opacity: props.opacity,
        visible: props.visible,
        highlightColor: props.highlightColor,
      });
    },
  });
};
